import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useHistory } from "react-router";
import { useDispatch } from 'react-redux';
import { DetailsList, SelectionMode, Callout, TooltipHost, Spinner, SpinnerSize } from '@fluentui/react';
import { PrimaryButton, DefaultButton, IDropdownOption, Dropdown } from 'office-ui-fabric-react';
import { DependencyAddIcon, SignOutIcon, ShoppingCartIcon } from '@fluentui/react-icons-mdl2';
import IroniaPoints from './IroniaPoints';
import Moment from 'moment';
import { currencyFormatter, percentFormatter, decimalFormatter, longDecimalFormatter, setCurrency } from '../../../utils/numberFormatter';
import 'office-ui-fabric-react/dist/css/fabric.css';
import Auth from "../../../Auth/Auth";
import { parseNumber } from '../../../utils/toFixedDecimals';
import { ReactComponent as IncrementIcon } from '../../../images/increment.svg';
import { ReactComponent as DecrementIcon } from '../../../images/decrement.svg';
import AppendCartLine from '../../../utils/AppendCartLine';
import { IInitialRegisters, TCategories, TLevel2PositionRaw, TReporting, TSubcategories, TSubCategoriesPositions } from './types/PortfolioTypes';
import { IInfoFundPortfolio } from '../../../services/FundService';
import ThreeWayToggle from '../../../components/ThreeWayToggle/ThreeWayToggle';

type TRtbState = {
	key: string;
	name: string;
};

const documentsColumns: any =
[
    { key: 'name', name: 'Fichero', fieldName: 'name', minWidth: 200, isResizable: true },
    { key: 'link', name: 'Acción', fieldName: 'url', width: 50, isResizable: false },
];

const dateFormat = (stringDate: any) =>
{
    return Moment(stringDate).format('DD/MM/YYYY');
}

type PortfolioTableProps =
{
    reporting?: TReporting;
    funds?: IInfoFundPortfolio[];
    fundsforModelData: (items: any[]) => any[];
    fundsForSimulation: (items: IInitialRegisters[]) => void;
    isResumeWallet?: boolean;
    showActions?: boolean;
    showDocuments?: boolean;
    showIroniaPoints?: boolean;
    onCollectedItemsUpdate?: (items: any[]) => void;
}

const INITIAL_RTB_STATE: TRtbState = { key: "linearReturn1d", name: "Rtb 1d" }

const PortfolioTable = (props: PortfolioTableProps) =>
{
    const [rtbState, setRtbState] = useState<TRtbState>(INITIAL_RTB_STATE);
    const { isResumeWallet } = props;
    const [registers, setRegisters] = useState<IInitialRegisters[]>([]);
    const [isAttachmentsVisible, setIsAttachmentsVisible] = useState(false);
    const [selectedRegister, setSelectedRegister] = useState<any>({});
    const isActiveManagementPlan: boolean = Auth.isActiveManagementPlan();
    const [collectedItems, setCollectedItems] = useState<any[]>([]);
    const [isProcessingComplete, setIsProcessingComplete] = useState(false);
    const history = useHistory();
    const dispatch = useDispatch();
    const [sending, setSending] = useState("");
    var activePlan = Auth.getActivePlan();

    const handleClick = async (item: any) =>
    {
        setSending(item.isin);
        await AppendCartLine(
            dispatch,
            history,
            {
                fund: item,
                amount: 0
            }
        );
        setSending("");
    };

    const stylesCenter = {
        cellTitle: { justifyContent: 'center', display: 'flex' } 
    };

	const onChangeRtb = (toggleValue: boolean | undefined) => {
		setRtbState({
			key: toggleValue ? "linearReturn1d" : "linearReturnYtd",
			name: toggleValue ? "Rtn 1d" : "Rtn YTD",
		})
	};

    const columns = [
        { key: 'name', name: '', isCollapsible: false, fieldName: 'name', minWidth: 150, maxWidth: 350, isResizable: true },
        ...(props.showDocuments ? [{ key: 'documents', name: 'Documentos', minWidth: 80, maxWidth: 80, isResizable: false, styles: stylesCenter }] : []),
        { key: 'titles', name: 'Títulos', fieldName: 'titles', minWidth: 70, maxWidth: 70, isResizable: false, styles: stylesCenter },
        { key: 'bought', name: 'Comprado', fieldName: 'bought', minWidth: 70, maxWidth: 70, isResizable: false, styles: stylesCenter },
        { key: 'valuation', name: 'Valoración', fieldName: 'valuation', minWidth: 70, maxWidth: 70, isResizable: false, styles: stylesCenter },
        { key: 'revaluation', name: 'Revalorización', fieldName: 'revaluation', minWidth: 100, maxWidth: 100, isResizable: false, styles: stylesCenter },
        { key: 'revaluationPercentage', name: '% Rev.', fieldName: 'revaluationPercentage', minWidth: 50, maxWidth: 50, isResizable: false, styles: stylesCenter },
		{
			key: rtbState.key, name: rtbState.name,
			fieldName: rtbState.key, minWidth: 120, maxWidth: 160, isResizable: false, styles: {
				cellTitle: {
					justifyContent: 'center', display: 'flex',
					color: "white",
					width: "140px"

				}
			},
			onRenderHeader: () => (
				<div className="rentabilidadToggle">
					<span>Rtb</span>
					<ThreeWayToggle
						onChange={onChangeRtb}
						toggleInitialState={rtbState.key === "linearReturn1d" ? 1 : 0}
						disableMiddlePosition
						toggleTexts={{
							turnOnText: "1d",
							turnOffText: "YTD"
						}}
					/>
				</div>
			),

		},
        { key: 'volatilityOneYear', name: 'Vola 1Y', fieldName: 'volatilityOneYear', minWidth: 50, maxWidth: 50, isResizable: false, styles: stylesCenter },
        { key: 'weight', name: 'Peso', fieldName: 'weight', minWidth: 50, maxWidth: 50, isResizable: false, styles: stylesCenter },
        { key: 'pmpc', name: 'PMPC', fieldName: 'pmpc', minWidth: 50, maxWidth: 70, isResizable: true, styles: stylesCenter },
        { key: 'vLiquid', name: 'V. liq', fieldName: 'vLiquid', minWidth: 50, maxWidth: 150, isResizable: true, styles: stylesCenter },
        { key: 'exchangeRate', name: 'TC', fieldName: 'exchangeRate', minWidth: 50, maxWidth: 50, isResizable: false, styles: stylesCenter },
        { key: 'liquidDate', name: 'Fecha VL', fieldName: 'liquidDate', minWidth: 80, maxWidth: 80, isResizable: false, styles: stylesCenter },
        ...(props.showActions ?
        [{
            key: 'actions', name: 'Acciones', fieldName: 'actions', minWidth: 110, maxWidth: 110, isResizable: false, styles: stylesCenter,
            onRender: (item: any) =>
            {
                if(!item?.isin || isResumeWallet === true || activePlan.outdatedContract)
                {
                    return (<></>);
                }
                return(
                    <React.Fragment>
                        {isActiveManagementPlan === true &&
                            (
                                <React.Fragment>
                                    <TooltipHost content="Reembolso" id={`refundBtn_${item.isin}`} styles={{root: { display: 'inline-block', paddingLeft: '2px' }}} calloutProps={{ gapSpace: 0 }}>
                                        <Link to={{pathname: '/portfolio/refunds', state: { origin: item }}}>
                                            <PrimaryButton className="light-circled-icon" id={`refundBtn_${item.isin}`}>
                                                <SignOutIcon className="ironia-table-icon" />
                                            </PrimaryButton>
                                        </Link>
                                    </TooltipHost>
                                </React.Fragment>)
                        }
                        {isActiveManagementPlan === false &&
                        (
                            <React.Fragment>
                                {item.available === true && (<TooltipHost content="Carrito" id={`cartBtn_${item.isin}`} styles={{root: { display: 'inline-block', paddingLeft: '2px' }}} calloutProps={{gapSpace: 0}}>
                                    <PrimaryButton className="light-circled-icon"
                                        id={`cartBtn_${item.isin}`}
                                        onClick={() => handleClick(item)}
                                        disabled={sending === item.isin || item.available === false}
                                    >
                                        {sending === item.isin && <Spinner size={SpinnerSize.small} className="button-spinner" />}
                                        {sending !== item.isin && <ShoppingCartIcon className="ironia-table-icon" shopping-cart />}
                                    </PrimaryButton>
                                </TooltipHost>)}
                                {props.funds && props.funds?.find(x => x.isin === item.isin)?.switchable === true && (<TooltipHost content="Traspasar" id={`transferBtn_${item.isin}`} styles={{ root: { display: 'inline-block', paddingLeft: '2px' } }} calloutProps={{ gapSpace: 0 }}>
                                    <Link to={{pathname: '/internal-switch', state: { origin: item }}}>
                                        <PrimaryButton className="light-circled-icon" id={`transferBtn_${item.isin}`}>
                                            <DependencyAddIcon className="ironia-table-icon" />
                                        </PrimaryButton>
                                    </Link>
                                </TooltipHost>)}
                                <TooltipHost content="Reembolso" id={`refundBtn_${item.isin}`} styles={{ root: { display: 'inline-block', paddingLeft: '2px' } }} calloutProps={{ gapSpace: 0 }}>
                                    <Link to={{pathname: '/portfolio/refunds', state: { origin: item }}}>
                                        <PrimaryButton className="light-circled-icon" id={`refundBtn_${item.isin}`}>
                                            <SignOutIcon className="ironia-table-icon" />
                                        </PrimaryButton>
                                    </Link>
                                </TooltipHost>
                            </React.Fragment>
                        )}
                    </React.Fragment>
                );
            }
        }] : []),
        ...(props.showIroniaPoints ? [{ key: 'ironiaPoints', name: 'Puntos IronIA', fieldName: 'score', minWidth: 140, maxWidth: 200, isResizable: false }] : [])
    ];

    useEffect(() =>
    {
        const valuation = props.reporting?.positions.positionsTo.aggregation.reduce((total: any, aggregation: any) =>
        {
            return total += aggregation.total.value;
        }, 0);

        const plusMinus = props.reporting?.positions.positionsTo.aggregation.reduce((total: any, aggregation: any) =>
        {
            return total += aggregation.total.plusMinus;
        }, 0);

        const buyAmount = valuation - plusMinus;

        const growth = buyAmount === 0 ? 0 : (valuation - buyAmount) / buyAmount;

        const weight = props.reporting?.positions.positionsTo.aggregation.reduce((total: any, aggregation: any) =>
        {
            return total += aggregation.total.weight;
        }, 0);

        const categories: TCategories[] = [];

        props.reporting?.positions.positionsTo.aggregation.forEach((aggregation: any) =>
        {
            const subcategories: TSubcategories[] = [];
            aggregation.level2.forEach((level2: any) =>
            {
                const subCategoryPositions: TSubCategoriesPositions[] = [];
                level2.positions.forEach((position: TLevel2PositionRaw) =>
                {
                    if(!position.hasOwnProperty('exchangeRate'))
                    {
                        position['exchangeRate'] = { value: 1 };
                    }
                    else
                    {
                        if(!position.exchangeRate.hasOwnProperty('value'))
                        {
                            position.exchangeRate['value'] = 1;
                        }
                    }

                    subCategoryPositions.push(
                    {
                        name: position.productName,
                        shortName: position.productShortName,
                        instrumentId: position.instrumentId,
                        isin: position.isin,
                        weight: position.weight,
                        titles: position.titles,
                        price: position.lastPriceInProductCurrency,
                        plusMinus: position.plusMinus,
                        plusMinusPercentage: position.plusMinusPercentage,
                        value: position.value,
                        instrumentType: position.instrumentType,
                        valuation: position.value,
                        buyAmount: position.value === 0 || position.value == null ? 0 : position.value - position.plusMinus,
                        exchangeRate: position.exchangeRate.value,
                        from: position.exchangeRate.from !== undefined ? position.exchangeRate.from : "",
                        lastPriceDate: position.dateLastPriceAsString,
                        currency: position.currency
                    });
                });

                const level2PlusMinus: number = level2.total.plusMinus;
                const level2PlusMinusPercentage: number = level2.total.plusMinusPercentage;
                const level2BuyAmount: number = level2.total.value === 0 || level2.total.value === null ? 0 : level2.total.value - level2.total.plusMinus;
                const level2Valuation: number = level2.total.value;
                const level2Growth: number = level2BuyAmount === 0 ? 0 : (level2Valuation - level2BuyAmount) / level2BuyAmount;
                const level2Weight: number = level2.total.weight;

                subcategories.push(
                {
                    name: level2.data.name,
                    positions: subCategoryPositions,
                    buyAmount: level2BuyAmount,
                    valuation: level2Valuation,
                    plusMinus: level2PlusMinus,
                    plusMinusPercentage: level2PlusMinusPercentage,
                    growth: level2Growth,
                    weight: level2Weight
                });
            });

            const aggregationBuyAmount: number = aggregation.total.value === 0 || aggregation.total.value === null ? 0 : aggregation.total.value - aggregation.total.plusMinus;
            const aggregationValuation: number = aggregation.total.value;
            const aggregationWeight: number = aggregation.total.weight;
            const aggregationPlusMinus: number = aggregation.total.plusMinus;
            const aggregationPlusMinusPercentage: number = aggregation.total.plusMinusPercentage;
            const aggregationGrowth: number = aggregationBuyAmount === 0 ? 0 : (aggregationValuation - aggregationBuyAmount) / aggregationBuyAmount;

            const categoriesPosition: TSubCategoriesPositions[] = [];
            if(aggregation.level2.length === 0)
            {
                aggregation.positions.forEach((position: TLevel2PositionRaw) =>
                {
                    if(position.instrumentType !== 21)
                    {
                        var date = new Date(position.date);
                        var dateAsString = String(date.getDate()).padStart(2, '0') + '/' + String(date.getMonth() + 1).padStart(2, '0') + '/' + date.getFullYear();

                        categoriesPosition.push(
                        {
                            name: "Operación pendiente (EUR)",
                            shortName: position.productShortName,
                            instrumentId: position.instrumentId,
                            isin: position.isin !== null ? position.isin : '',
                            weight: position.weight,
                            titles: position.titles,
                            price: undefined,
                            plusMinus: currencyFormatter.format(position.plusMinus),
                            plusMinusPercentage: position.plusMinusPercentage,
                            value: currencyFormatter.format(position.value),
                            instrumentType: position.instrumentType,
                            valuation: currencyFormatter.format(position.value === 0 || position.value === null ? 0 : position.value - position.plusMinus),
                            exchangeRate: null,
                            from: null,
                            lastPriceDate: dateAsString,
                            currency: position.currency,
                        });
                    }
                    else
                    {
                        categoriesPosition.push(
                        {
                            name: position.productName,
                            shortName: position.productShortName,
                            instrumentId: position.instrumentId,
                            isin: position.isin,
                            weight: position.weight,
                            titles: position.titles,
                            price: position.lastPriceInProductCurrency,
                            plusMinus: position.plusMinus,
                            plusMinusPercentage: position.plusMinusPercentage,
                            value: position.value,
                            instrumentType: position.instrumentType,
                            valuation: position.value,
                            buyAmount: position.value === 0 || position.value == null
                                ? 0
                                : position.value - position.plusMinus,
                            exchangeRate: position.exchangeRate.value !== undefined
                                ? position.exchangeRate.value
                                : position.exchangeRate,
                            from: position.exchangeRate.from !== undefined
                                ? ""
                                : position.exchangeRate.from,
                            lastPriceDate: position.dateLastPriceAsString
                        });
                    }
                });
            }

            categories.push(
            {
                name: aggregation.data.name,
                subcategories: subcategories,
                buyAmount: aggregationBuyAmount,
                valuation: aggregationValuation,
                weight: aggregationWeight,
                plusMinus: aggregationPlusMinus,
                plusMinusPercentage: aggregationPlusMinusPercentage,
                growth: aggregationGrowth,
                positions: categoriesPosition
            });
        });

        let initialRegisters: IInitialRegisters[] =
        [
            {
                name: 'Total Cartera',
                documents: [],
                titles: '-',
                bought: !isNaN(buyAmount) ? currencyFormatter.format(buyAmount) : '-',
                valuation: !isNaN(valuation) ? currencyFormatter.format(valuation) : '-',
                revaluation: !isNaN(plusMinus) ? currencyFormatter.format(plusMinus) : '-',
                revaluationPercentage: !isNaN(growth) ? percentFormatter.format(growth) : '-',
                weight: weight ? percentFormatter.format(weight) : '-',
                pmpc: '-',
                vLiquid: '-',
                exchangeRate: '-',
                liquidDate: '-',
                ironiaPoints: '-',
                actions: '-',
                type: 'total',
                expanded: false,
                hidden: false,
                category: '-',
                currency: ''
            }
        ];

        categories.forEach((category: TCategories) =>
        {
            initialRegisters.push(
            {
                name: category.name ? category.name : '-',
                documents: [],
                titles: '-',
                bought: !isNaN(category.buyAmount) ? currencyFormatter.format(category.buyAmount) : '-',
                valuation: !isNaN(category.valuation) ? currencyFormatter.format(category.valuation) : '-',
                revaluation: !isNaN(category.plusMinus) ? currencyFormatter.format(category.plusMinus) : '-',
                revaluationPercentage: !isNaN(category.plusMinusPercentage) ? percentFormatter.format(category.plusMinusPercentage) : '-',
                weight: category.weight ? percentFormatter.format(category.weight) : '-',
                pmpc: '-',
                vLiquid: '-',
                exchangeRate: '-',
                liquidDate: '-',
                ironiaPoints: '-',
                actions: '-',
                type: 'category',
                hidden: false,
                expanded: true,
                category: 'Total',
                currency: '-',
            });

            category.positions.forEach((position: TSubCategoriesPositions) =>
            {
                if(position.instrumentType !== 21)
                {
                    initialRegisters.push(
                    {
                        name: position.name,
                        isin: position.isin,
                        documents: [],
                        titles: '-',
                        bought: position.value,
                        valuation: position.value,
                        revaluation: undefined,
                        revaluationPercentage: '-',
                        weight: position.weight ? percentFormatter.format(position.weight) : '-',
                        pmpc: '-',
                        vLiquid: position.value,
                        exchangeRate: '-',
                        liquidDate: position.lastPriceDate,
                        ironiaPoints: '-',
                        actions: '-',
                        type: 'position',
                        hidden: false,
                        category: category.name ? category.name : '',
                        score: '-',
                        instrumentId: position.instrumentId,
                        allFundsId: undefined,
                        benchmarkFinametrixId: undefined,
                        currency: position.currency
                    });
                }
                else
                {
                    let fund = props.funds?.find((fund: any) => fund.isin === position.isin);
                    if(position.buyAmount)
                    {
                        initialRegisters.push(
                        {
                            name: position.name,
                            isin: position.isin,
                            available: fund ? fund.available : true,
                            documents: fund ? fund.documents : [],
                            titles: position.titles ? longDecimalFormatter.format(position.titles) : '-',
                            bought: !isNaN(position.buyAmount) ? currencyFormatter.format(position.buyAmount) : '-',
                            valuation: !isNaN(position.valuation) ? currencyFormatter.format(position.valuation) : '-',
                            revaluation: !isNaN(position.plusMinus) ? currencyFormatter.format(position.plusMinus) : '-',
                            revaluationPercentage: !isNaN(position.plusMinusPercentage) ? percentFormatter.format(position.plusMinusPercentage) : '-',
                            linearReturnYtd: fund?.linearReturnYtd !== undefined ? percentFormatter.format(fund.linearReturnYtd) : '-',
                            linearReturn1d: fund?.linearReturn1d !== undefined ? percentFormatter.format(fund.linearReturn1d) : '-',
                            volatilityOneYear: fund?.volatilityOneYear !== undefined ? percentFormatter.format(fund.volatilityOneYear) : '-',
                            weight: position.weight ? percentFormatter.format(position.weight) : '-',
                            pmpc: !isNaN(position.buyAmount) && position.titles ? currencyFormatter.format(position.buyAmount / position.titles) : '-',
                            vLiquid: position.price ? setCurrency(position.from, position.price) : '-',
                            exchangeRate: position.exchangeRate?.value === undefined ? '-' : decimalFormatter.format(position.exchangeRate.value),
                            liquidDate: position.lastPriceDate ? dateFormat(position.lastPriceDate) : '-',
                            ironiaPoints: '-',
                            actions: '-',
                            type: 'position',
                            hidden: false,
                            category: category.name ? category.name : '',
                            score: fund ? fund.averageScore : 0,
                            finametrixId: position.instrumentId,
                            instrumentId: position.instrumentId,
                            instrumentType: position.instrumentType,
                            allFundsId: fund ? fund.allFundsId : '',
                            benchmarkFinametrixId: fund ? fund.benchmarkFinametrixId : '',
                            managementFee: fund ? fund.managementFee : undefined,
                            noMin: fund ? fund.noMin : undefined,
                            minimumInitialInvestment: fund ? fund.minimumInitialInvestment : undefined,
                            ongoingCharges: fund ? fund.ongoingCharges : undefined,
                            rebate: fund ? fund.rebate : undefined,
                            complexity: fund ? fund.complexity : false,
                            minimumAdditionalInvestment: fund ? fund.minimumAdditionalInvestment : undefined,
                            cleanShare: fund ? fund.cleanShare : false,
                            currency: position.currency
                        });
                    }
                }
            });

            category.subcategories.forEach((subcategory: TSubcategories) =>
            {
                initialRegisters.push(
                {
                    name: subcategory.name,
                    documents: [],
                    titles: '-',
                    bought: !isNaN(subcategory.buyAmount) ? currencyFormatter.format(subcategory.buyAmount) : '-',
                    valuation: !isNaN(subcategory.valuation) ? currencyFormatter.format(subcategory.valuation) : '-',
                    revaluation: !isNaN(subcategory.plusMinus) ? currencyFormatter.format(subcategory.plusMinus) : '-',
                    revaluationPercentage: !isNaN(subcategory.plusMinusPercentage) ? percentFormatter.format(subcategory.plusMinusPercentage) : '-',
                    weight: subcategory.weight ? percentFormatter.format(subcategory.weight) : '-',
                    pmpc: '-',
                    vLiquid: '-',
                    liquidDate: '-',
                    exchangeRate: '-',
                    ironiaPoints: '-',
                    actions: '-',
                    type: 'subcategory',
                    hidden: false,
                    expanded: true,
                    category: category.name ? category.name : '',
                });

                subcategory.positions.forEach((position: TSubCategoriesPositions) =>
                {
                    let fund = props.funds?.find((fund: any) => fund.isin === position.isin);
                    let exchangeRate = position.exchangeRate.value !== undefined ? position.exchangeRate.value : position.exchangeRate;
                    initialRegisters.push(
                    {
                        name: position.name,
                        isin: position.isin,
                        available: fund ? fund.available : true,
                        documents: fund ? fund.documents : [],
                        titles: position.titles ? longDecimalFormatter.format(position.titles) : '-',
                        bought: !isNaN(position.buyAmount) ? currencyFormatter.format(position.buyAmount) : '-',
                        valuation: !isNaN(position.valuation) ? currencyFormatter.format(position.valuation) : '-',
                        revaluation: !isNaN(position.plusMinus) ? currencyFormatter.format(position.plusMinus) : '-',
                        revaluationPercentage: !isNaN(position.plusMinusPercentage) ? percentFormatter.format(position.plusMinusPercentage) : '-',
                        linearReturnYtd: fund?.linearReturnYtd !== undefined ? percentFormatter.format(fund.linearReturnYtd) : '-',
                        linearReturn1d: fund?.linearReturn1d !== undefined ? percentFormatter.format(fund.linearReturn1d) : '-',
                        volatilityOneYear: fund?.volatilityOneYear !== undefined ? percentFormatter.format(fund.volatilityOneYear) : '-',
                        weight: position.weight ? percentFormatter.format(position.weight) : '-',
                        pmpc: !isNaN(position.buyAmount) && position.titles ? currencyFormatter.format(position.buyAmount / position.titles) : '-',
                        vLiquid: position.price ? setCurrency(position.from, position.price) : '-',
                        exchangeRate: exchangeRate === 1 ? (1 / exchangeRate) : decimalFormatter.format(1 / exchangeRate),
                        liquidDate: dateFormat(position.lastPriceDate),
                        ironiaPoints: '-',
                        actions: '-',
                        type: 'position',
                        hidden: false,
                        category: category.name ? category.name : '',
                        subcategory: subcategory.name,
                        score: fund ? fund.averageScore : 0,
                        finametrixId: position.instrumentId,
                        instrumentType: position.instrumentType,
                        instrumentId: position.instrumentId,
                        allFundsId: fund ? fund.allFundsId : '',
                        benchmarkFinametrixId: fund ? fund.benchmarkFinametrixId : '',
                        managementFee: fund ? fund.managementFee : undefined,
                        noMin: fund ? fund.noMin : false,
                        minimumInitialInvestment: fund ? fund.minimumInitialInvestment : undefined,
                        ongoingCharges: fund ? fund.ongoingCharges : undefined,
                        rebate: fund ? fund.rebate : undefined,
                        complexity: fund ? fund.complexity : false,
                        minimumAdditionalInvestment: fund ? fund.minimumAdditionalInvestment : undefined,
                        cleanShare: fund ? fund.cleanShare : false,
                        currency: position.currency
                    });
                });
            });
        });

        props.fundsforModelData(initialRegisters.filter(x => x.documents && x.type === "position" && x.documents.length > 0));

        // Esta salida es exclusivamente para los datos de simulación, los cuales no necesitan documentos
		props.fundsForSimulation(initialRegisters.filter(x => x.type === "position"));

        setRegisters(initialRegisters);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [props.funds, props.reporting?.positions.positionsTo.aggregation]);

    const toggleCategory = (categoryName: any) =>
    {
        const foundItem = registers.find((item: any) => item !== undefined && item.type === 'category' && item.name === categoryName);
        const hidden: boolean | undefined = foundItem ? foundItem.expanded : false;

        setRegisters(registers.map(function callback(item, index, array)
        {
            switch(item.type)
            {
                case "category":
                    if(item.name === categoryName)
                    {
                        item.expanded = !item.expanded;
                    }
                    break;
                case "subcategory":
                    if(item.category === categoryName)
                    {
                        item.hidden = hidden;
                    }
                    break;
                case "position":
                    if(item.category === categoryName)
                    {
                        var subcategory = registers.find(register => register.type === 'subcategory' && register.name === item.subcategory);
                        if(subcategory)
                        {
                            item.hidden = hidden || !subcategory.expanded;
                        }
                        else
                        {
                            item.hidden = hidden;
                        }
                    }
                    break;
                default:
                    break;
            }

            return item;
        }));
    };
    const toggleSubcategory = (categoryName: any) =>
    {
        const foundItem = registers.find((item: IInitialRegisters) => item !== undefined && item.type === 'subcategory' && item.name === categoryName);

        const hidden: boolean | undefined = foundItem ? foundItem.expanded : false;

        setRegisters(registers.map(function callback(currentValue: any, index: any, array: any)
        {
            if(currentValue.type === "subcategory")
            {
                if(currentValue.name === categoryName)
                {
                    currentValue.expanded = !currentValue.expanded;
                }
            }
            else
            {
                if(currentValue.subcategory === categoryName)
                {
                    currentValue.hidden = hidden;
                }
            }

            return currentValue;
        }));

    };
    const toggleAttachmentsVisible = (isin: string) =>
    {
        const register: any = registers.find((item: any) => item.isin === isin);
        setSelectedRegister(register);
        setIsAttachmentsVisible(true);
    };

    // useEffect para notificar al padre sobre collectedItems
    useEffect(() =>
    {
        if(props.onCollectedItemsUpdate)
        {
            props.onCollectedItemsUpdate(collectedItems);
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [collectedItems, props.onCollectedItemsUpdate]);

    // Recolectar los datos de la tabla evitando actualizar el estado dentro de funciones de renderizado.
    useEffect(() =>
    {
        const visibleRegisters = registers.filter((register) => !register.hidden);

        setCollectedItems((prevItems) =>
        {
            const itemsToAdd = visibleRegisters.filter((item) => !prevItems.some((collectedItem) => collectedItem.name === item.name));

            if(itemsToAdd.length > 0)
            {
                setIsProcessingComplete(true);
                return [...prevItems, ...itemsToAdd];
            }

            setIsProcessingComplete(true);
            return prevItems;
        });
    }, [registers]);

    const portfolioCategoryAndSubcategory = (item: { isin: string, type: string, name: string, expanded: string }, kind: string) =>
    {
        return(
            <span className={"column-title " + item.type}>
                <PrimaryButton onClick={() => kind === 'category' ? toggleCategory(item.name) : toggleSubcategory(item.name)}className="toggleGroupVisibility">
                    <i className={"ironia-icon " + (item.expanded ? "keyboard-arrow-up" : "keyboard-arrow-down")} />
                </PrimaryButton>
                {item.name}
            </span>
        )
    }

    const renderItemColumn = (item: any, index: any, column: any) =>
    {
        const fieldContent = item[column.fieldName];
        switch(column.key)
        {
            case "name":
                switch(item.type)
                {
                    case "position":
                        return(
                            <React.Fragment>
                                <Link to={`/${item.isin}`}>
                                    <span className={"column-title " + item.type}>
                                        {item.name}
                                    </span>
                                </Link>
                                <br />
                                <span className={"column-title " + item.type}>
                                    {item.isin}
                                </span>
                            </React.Fragment>
                        );
                    case "category":
                        return(portfolioCategoryAndSubcategory(item, 'category'));
                    case "subcategory":
                        return(portfolioCategoryAndSubcategory(item, 'subCategory'));
                    default:
                        return(
                            <span className={"column-title " + item.type}>
                                {item.name}
                            </span>
                        );
                }
            case "documents":
                if(item.documents === undefined || item.documents.length === 0)
                {
                    return "";
                }
                else
                {
                    return(
                        <TooltipHost content="Documentos"id={`buttonId${item.isin}`}
                            styles={{root: { display: "inline-block" }}}
                            calloutProps={{gapSpace: 0}}
                        >
                            <PrimaryButton className="light-circled-icon document-icon"
                                id={"buttonId" + item.isin}
                                onClick={() => toggleAttachmentsVisible(item.isin)}
                            >
                                <i className={"ironia-icon pdf"} />
                            </PrimaryButton>
                        </TooltipHost>
                    );
                }
            case "ironiaPoints":
                return <IroniaPoints score={item.score} />;
            case "titles":
            case "bought":
            case "valuation":
            case "revaluation":
            case "weight":
            case "pmpc":
            case "vLiquid":
            case "exchangeRate":
            case "liquidDate":
            case "volatilityOneYear":
                return(
                    <span className={item.type + " table-numeric-column"}>
                        {fieldContent}
                    </span>
                );
            case "revaluationPercentage":
            case "linearReturnYtd":
            case "linearReturn1d":
                if(fieldContent === undefined)
                {
                    return '-';
                }
                let valueRtb = parseNumber(fieldContent, 2);
                return(
                    <span className={item.type + " table-numeric-column"}>

                        {fieldContent} {valueRtb > 0 && <IncrementIcon />} {valueRtb < 0 && <DecrementIcon />}
                    </span>
                );
            default:
                return <span className={item.type}>{fieldContent}</span>;
        }
    };

    const renderAttachmentColumn = (item: any, index: any, column: any) =>
    {
        switch(column.key)
        {
            case 'link':
                return (<a href={item.url} target="_blank" rel="noreferrer"><i className={'ironia-icon downloads'} /> Descargar</a>)
            default: return (item[column.key])
        }
    }

    const fundPositions = props.reporting?.positions.positionsTo.positions.filter((pos: any) => pos.isin !== null);
    if(fundPositions?.length === 0)
    {
        return(
            <p className="description">
                No hay fondos en la cartera seleccionada
            </p>
        );
    }
    else
    {
        if(registers.length < 2)
        {
            return(
                <>
                    <Spinner size={SpinnerSize.large} />
                </>
            );
        }

        return(
            <React.Fragment>
                <div className={`ms-Grid-col ms-sm12`}>
                    <DetailsList items={registers.filter((register: any) => !register.hidden)}
                        columns={columns}
                        selectionMode={SelectionMode.none}
                        onRenderRow={(props: any, defaultRender: any) =>
                        {
                            return defaultRender({...props, className: props.item.type})
                        }}
                        onRenderItemColumn={renderItemColumn}
                    />
                </div>
                {isAttachmentsVisible &&
                (
                    <Callout gapSpace={0} target={`#buttonId${selectedRegister.isin}`} onDismiss={() => setIsAttachmentsVisible(false)} setInitialFocus isBeakVisible={false}>
                        <DefaultButton style={{ marginTop: '8px' }} onClick={() => setIsAttachmentsVisible(false)} className="closeButton">
                            <i className='ironia-icon cross' />
                        </DefaultButton>
                        <h4 style={{ padding: "0px 20px 0px 20px" }}>Documentos {selectedRegister.name}</h4>
                        <DetailsList
                            items={selectedRegister.documents}
                            columns={documentsColumns}
                            onRenderItemColumn={renderAttachmentColumn}
                            selectionMode={SelectionMode.none}
                        />
                    </Callout>
                )}
            </React.Fragment>
        );
    }
};

export default PortfolioTable;